import { yupResolver } from '@hookform/resolvers/yup';
import { Box, CircularProgress, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ClientDTO,
  Field,
  FieldTypeEnum,
  findByName,
  getAllClientCustomFields,
  getClientById,
  saveAndUpdateClient,
  saveActivityLog,
  LogAction,
  LogContextType,
  ClientStatusDTO,
  getClientStatusListByVisibility,
  csrfTokenCall,
  IUserDTO,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  WsButton,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { object, string } from 'yup';

import WsTagsFilter from '../../../components/filters/components/WsTagsFilter';
import { useUserContext } from '../../../context';
import {
  getAvEditAddDesUiName,
  activityLogDTOHelper,
} from '../../../features/activityLog/ActivityHelper';
import { fetchAndSetAssignedUsers } from '../../../features/users/helper/hooks';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getJobIndustries } from '../../jobs/helper/JobHelperService';
import { defaultClientDTO, linkedInRegex, websiteRegex } from '../helper/ClientListingHelper';

import CustomField from './CustomField';

const validationSchema = object().shape({
  name: string().required('Please provide Name'),
  linkedinUrl: string().nullable().notRequired().matches(linkedInRegex, {
    excludeEmptyString: true,
    message: 'Please provide a valid LinkedIn URL',
  }),
  website: string()
    .nullable()
    .notRequired()
    .matches(websiteRegex, 'Please enter a valid website URL'),
});

interface ClientDetailsFormProps {
  setFormWizardCurrentStep?: (step: number) => void;
  parentId?: string;
  setClientId?: (clientId: string) => void;
  getClient?: (client: ClientDTO) => void;
  defaultValues?: ClientDTO;
  source?: 'JobPostingForm' | 'ClientAdditionForm';
}

const ClientDetailsForm = ({
  setFormWizardCurrentStep,
  parentId,
  setClientId,
  defaultValues = defaultClientDTO,
  source = 'ClientAdditionForm',
  getClient,
}: ClientDetailsFormProps) => {
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      customFields: defaultValues.customFields ?? {},
      clientStatusId: defaultValues.clientStatusId,
      clientStatusReason: defaultValues.clientStatusReason,
      clientStatusName: defaultValues.clientStatus?.clientStatusName,
      tags: defaultValues.tags ?? [],
    },
  });
  const [isDuplicateName, setIsDuplicateName] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [selectedParent, setSelectedParent] = useState<ClientDTO>();
  const [customFields, setCustomFields] = useState<Field[]>();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [isLoading, setIsLoading] = useState(false);

  const [ourClientStatusOptions, setOurClientStatusOptions] = useState<WsSelectObj[]>([]);
  const [defaultClientStatusName, setDefaultClientStatusName] = useState<string>(
    defaultValues?.clientStatus ? defaultValues?.clientStatus?.clientStatusName : ''
  );
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { dirtyFields },
  } = methods;

  useEffect(() => {
    (async () => {
      const { data } = await getAllClientCustomFields();
      setCustomFields(data);
    })();
  }, []);

  useEffect(() => {
    const loadParent = async (id: string) => {
      if (id) {
        const response = await getClientById(id);
        if (response.data.data) {
          setSelectedParent(response.data.data);
          setDefaultClientStatusName(response.data.data.clientStatusName);
          setValue('parentId', response.data.data.name);
        }
      }
    };
    defaultValues?.parentId && loadParent(defaultValues.parentId);
    parentId && loadParent(parentId);
  }, [defaultValues, setValue, parentId]);

  const onSubmit = async (data: ClientDTO) => {
    if (isDuplicateName) {
      setNotification(failed('Client already exists for this name!!'));
      return;
    }
    if (selectedParent) {
      data.parentId = selectedParent?.value ?? selectedParent?.id;
    }

    if (data.clientStatusId != null) {
      data.clientStatus = {
        id: data?.clientStatusId,
        clientStatusName: data?.clientStatus?.clientStatusName ?? '',
      };
    }
    setIsLoading(true);
    try {
      const avPayload = { ...activityLogDTOHelper };
      const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
      const allowedRecruiters = await fetchAndSetAssignedUsers(loggedInUser.email ?? '');
      data.allowedRecruiters = allowedRecruiters;
      const response = await saveAndUpdateClient(data);
      if (response.data.data) {
        setClientId && setClientId(response.data.data.id);
        getClient && getClient(response.data.data);
      }
      if (data) {
        if (data.id) {
          avPayload.action = LogAction.Edit;
          const avEmptyEntityfields = Object.keys(
            Object.entries(defaultValues).reduce((acc, [k, v]) => {
              if (v === '' || v === null) acc[k] = v;
              return acc;
            }, {})
          );
          avPayload.description = `${getAvEditAddDesUiName(
            'Client',
            avEmptyEntityfields,
            avChangedValueViaDirtyFileds
          )} under`;
        } else {
          avPayload.action = LogAction.Add;
          avPayload.description = `ADDED`;
        }
        avPayload.parentEntitiyType = LogContextType.Client;
        avPayload.entityId = response.data.data.id;
        avPayload.parentEntitiyId = response.data.data.id;
        avPayload.field = 'Client';
        avPayload.entityName = response.data.data.name;
        avPayload.contextData = { clientId: response.data.data.id };
        avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
      }
      setNotification(success('Client has been saved successfully !!'));
    } catch (error) {
      setNotification(failed('Something went wrong, Please try again !!'));
    }
    setFormWizardCurrentStep && setFormWizardCurrentStep(1);
    setIsLoading(false);
  };

  const validateDuplicateClientName = async (e) => {
    const response = await findByName(e.target.value);
    setIsDuplicateName(response.data.data && response.data.data.id != getValues().id);
  };

  useEffect(() => {
    const getClientStatusListDetails = async () => {
      const response = await getClientStatusListByVisibility();
      const optionsArray: WsSelectObj[] = [];
      let defaultStatusName = '';
      response.data.forEach((obj: ClientStatusDTO) => {
        if (obj.makeDefaultStatus) {
          defaultStatusName = obj.id;
          methods.setValue('clientStatusId', obj.id);
        }
        optionsArray.push({
          label: obj.clientStatusName as string,
          value: obj.id ?? '',
        });
      });
      if (defaultValues.clientStatus?.clientStatusName) {
        const selectedOption = optionsArray.find(
          (item) => item.label === defaultValues.clientStatus?.clientStatusName
        );
        if (selectedOption) {
          methods.setValue('clientStatusId', selectedOption.value);
        }
      } else {
        methods.setValue('clientStatusId', defaultStatusName);
      }
      setOurClientStatusOptions(optionsArray);
    };

    getClientStatusListDetails();
  }, [defaultValues.clientStatus?.clientStatusName, methods]);

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);
  return customFields ? (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                <Box sx={{ flex: 1 }}>
                  <ControlledTextInput
                    name="name"
                    type="text"
                    fullWidth
                    labelText="Client Name"
                    variant="outlined"
                    showMandatorySymbol={true}
                    onBlur={validateDuplicateClientName}
                  />
                </Box>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsLocationSearch
                  required
                  multiSelect={false}
                  name="location"
                  placeHolder="Location"
                  showMandatorySymbol={true}
                  wisestepApi={true}
                  token={token}
                  authorization={auth}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsSelect
                  label="Client Status Name"
                  name="clientStatusId"
                  selectArray={ourClientStatusOptions}
                  defaultValue={defaultClientStatusName}
                  size="small"
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="website"
                  type="text"
                  fullWidth
                  labelText="Website"
                  variant="outlined"
                  required
                  showMandatorySymbol
                />
              </Grid>
              {source !== 'JobPostingForm' && (
                <>
                  <Grid item p={1} sm={5.75}>
                    <WsSelect
                      label="Industry Type"
                      name="industry"
                      defaultValue={defaultValues?.industry ?? ''}
                      selectArray={getJobIndustries()}
                      size="small"
                    />
                  </Grid>
                  <Grid item p={1} sm={5.75}>
                    <ControlledTextInput
                      name="source"
                      type="text"
                      fullWidth
                      labelText="Source"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item p={1} sm={5.75}>
                    <ControlledTextInput
                      name="linkedinUrl"
                      type="text"
                      fullWidth
                      labelText="Linkedin Url"
                      required
                      showMandatorySymbol
                    />
                  </Grid>
                  <Grid item p={1} sm={5.75}>
                    <WsTagsFilter placeHolder="Enter Tags" name="tags" label="Tags" />
                  </Grid>
                </>
              )}
              {source !== 'JobPostingForm' &&
                customFields.map(
                  (field) =>
                    field.visible && (
                      <Grid
                        item
                        key={field.id}
                        p={1}
                        sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 5.75}
                      >
                        <CustomField {...field} name={`customFields.${field.name}`} />
                      </Grid>
                    )
                )}
              <Grid item p={1} sm={11.5}>
                <ControlledTextArea name="description" labelText="Description" minRows={5} />
              </Grid>
            </Grid>
          </Box>
          <Grid container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <WsButton
                variant="contained"
                size="medium"
                type="submit"
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
              >
                {isLoading ? 'Saving...' : source === 'JobPostingForm' ? 'Save' : 'Continue'}
              </WsButton>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  ) : null;
};

export default ClientDetailsForm;
